import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Search, MapPin, User, Phone, Mail, FileText, Briefcase } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { createQuote, getConfig, listClients, createClient, updatePricingSettings, getPricingSettings } from "@/lib/api";
import { useIsMobile } from "@/components/custom/AppShell";

export default function NewJob() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);

  // Client
  const [clientSearch, setClientSearch] = useState("");
  const [clientResults, setClientResults] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);

  // Job fields
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [clientType, setClientType] = useState("Private");
  const [worksType, setWorksType] = useState("Quote Request");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [tenantPhone, setTenantPhone] = useState("");
  const [jobDetails, setJobDetails] = useState("");
  const [showAddClientType, setShowAddClientType] = useState(false);
  const [newTypeName, setNewTypeName] = useState("");
  const [newTypeCommission, setNewTypeCommission] = useState("none");
  const [newTypeCategory, setNewTypeCategory] = useState("corporate");

  // Address — Currys-style
  const [addressMode, setAddressMode] = useState("postcode"); // "postcode" | "manual" | "found"
  const [postcode, setPostcode] = useState("");
  const [postcodeError, setPostcodeError] = useState("");
  const [addressResults, setAddressResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [county, setCounty] = useState("");

  useEffect(() => { getConfig().then(setConfig).catch(() => {}); }, []);

  // Client search
  useEffect(() => {
    if (clientSearch.length < 1) { setClientResults([]); return; }
    const t = setTimeout(() => { listClients(clientSearch).then(setClientResults).catch(() => {}); }, 200);
    return () => clearTimeout(t);
  }, [clientSearch]);

  const selectClient = (c) => {
    setSelectedClient(c);
    setClientSearch("");
    setClientResults([]);
    setFirstName(c.first_name || "");
    setLastName(c.last_name || "");
    setClientType(c.client_type || "Private");
    setEmail(c.email || "");
    setPhone(c.phone || "");
  };

  // Find address by postcode — uses Ideal Postcodes for Royal Mail PAF data
  const findAddress = async () => {
    const clean = postcode.replace(/\s/g, "").toUpperCase();
    if (clean.length < 3) { setPostcodeError("Please enter a valid postcode"); return; }
    setPostcodeError("");
    setSearching(true);
    setAddressResults([]);
    try {
      const apiKey = process.env.REACT_APP_IDEAL_POSTCODES_KEY;
      const resp = await fetch(`https://api.ideal-postcodes.co.uk/v1/postcodes/${encodeURIComponent(clean)}?api_key=${apiKey}`);
      const data = await resp.json();
      if (data.code !== 2000 || !data.result || data.result.length === 0) {
        setPostcodeError("Postcode not found. Try entering manually.");
        setSearching(false);
        return;
      }
      const results = data.result.map(r => {
        const line1 = [r.line_1, r.line_2].filter(Boolean).join(", ");
        const line2 = r.line_3 || "";
        return {
          line1,
          line2,
          city: r.post_town || "",
          county: r.county || "",
          postcode: r.postcode || clean,
          display: [r.line_1, r.line_2, r.line_3, r.post_town, r.postcode].filter(Boolean).join(", "),
        };
      });
      setPostcode(data.result[0]?.postcode || clean);
      setCity(data.result[0]?.post_town || "");
      setCounty(data.result[0]?.county || "");
      setAddressResults(results);
    } catch {
      setPostcodeError("Lookup failed. Try entering manually.");
    } finally {
      setSearching(false);
    }
  };

  // Address autocomplete — same Nominatim approach as the Dashboard
  const addressSearchTimer = useRef(null);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const searchAddressAutocomplete = (query) => {
    if (addressSearchTimer.current) clearTimeout(addressSearchTimer.current);
    if (query.length < 3) { setAddressSuggestions([]); return; }
    addressSearchTimer.current = setTimeout(async () => {
      try {
        const resp = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&countrycodes=gb&format=json&addressdetails=1&limit=6`);
        const data = await resp.json();
        setAddressSuggestions(data.map(r => ({
          display: r.display_name,
          line1: [r.address?.house_number, r.address?.road].filter(Boolean).join(" ") || r.address?.road || r.display_name.split(",")[0],
          line2: r.address?.suburb || "",
          city: r.address?.city || r.address?.town || r.address?.suburb || r.address?.county || "",
          county: r.address?.county || "",
          postcode: r.address?.postcode || "",
        })));
      } catch { setAddressSuggestions([]); }
    }, 300);
  };

  const selectAddress = (addr) => {
    setAddressLine1(addr.line1);
    setAddressLine2(addr.line2 || "");
    setCity(addr.city);
    setCounty(addr.county || "");
    setPostcode(addr.postcode);
    setAddressResults([]);
    setAddressMode("found");
  };

  const handleCreate = async () => {
    if (!firstName.trim()) { toast.error("Client first name is required"); return; }
    if (!lastName.trim()) { toast.error("Client last name is required"); return; }
    if (!addressLine1.trim()) { toast.error("Address Line 1 is required"); return; }
    if (!postcode.trim()) { toast.error("Postcode is required"); return; }
    if (!clientType) { toast.error("Client type is required"); return; }
    if (!email.trim()) { toast.error("Correspondence email is required"); return; }
    if (!worksType) { toast.error("Works type is required"); return; }
    if (!jobDetails.trim()) { toast.error("Description is required"); return; }

    const combinedAddress = [addressLine1.trim(), addressLine2.trim(), city.trim(), county.trim(), postcode.trim()].filter(Boolean).join(", ");
    setSaving(true);
    try {
      if (!selectedClient) {
        await createClient({
          first_name: firstName.trim(), last_name: lastName.trim(), client_type: clientType,
          email: email.trim(), phone: phone.trim(),
          properties: combinedAddress ? [combinedAddress] : [],
        });
      }
      await createQuote({
        client_type: clientType, client_first_name: firstName.trim(), client_last_name: lastName.trim(),
        site_address: combinedAddress, correspondence_email: email.trim(),
        correspondence_phone: phone.trim(), tenant_phone: tenantPhone.trim(),
        job_details: jobDetails.trim(), works_type: worksType,
        labour_hours: 0, vat_enabled: true, status: "Not Started", start_date: "",
        materials_status: "Not Started", job_notes: "", quote_started: false, actions: [], measurements: [],
      });
      toast.success("Job created");
      navigate(-1);
    } catch {
      toast.error("Failed to create job");
    } finally {
      setSaving(false);
    }
  };

  const clientTypes = config?.client_types || ["Bairstow Eves", "Hamptons", "Private", "Gladstone", "Keatons"];

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      {/* Header */}
      <div className={`${isMobile ? "px-4 pt-4 pb-3" : "px-8 pt-6 pb-4"} sticky top-0 z-30`} style={{ background: "var(--bg)" }}>
        <div className={`flex ${isMobile ? "flex-col gap-2" : "items-center justify-between"}`}>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="btn-secondary h-9" data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <h1 className={isMobile ? "text-base font-semibold" : "text-xl font-semibold"} style={{ color: "var(--text-primary)" }}>New Job</h1>
          </div>
          <Button onClick={handleCreate} disabled={saving} className={`btn-primary gap-2 ${isMobile ? "h-9 w-full" : "h-10 px-6"}`} data-testid="new-job-submit">
            <Plus className="w-4 h-4" /> {saving ? "Creating..." : "Create Job"}
          </Button>
        </div>
      </div>

      <div className={`max-w-3xl mx-auto ${isMobile ? "px-4 py-3" : "px-8 py-4"}`}>

        {/* Client Section */}
        <section className="ui-surface p-6 mb-6" data-testid="new-job-client-section">
          <h2 className="text-sm font-semibold tracking-wide uppercase mb-5" style={{ color: "var(--text-secondary)" }}>
            <User className="w-4 h-4 inline mr-2" style={{ color: "var(--text-muted)" }} /> Client
          </h2>

          {/* Client search */}
          {selectedClient ? (
            <div className="flex items-center gap-3 p-3 rounded-lg mb-5" style={{ background: "var(--bg)", border: "1px solid var(--border)" }}>
              <div className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0" style={{ background: "var(--primary)" }}>
                {selectedClient.first_name?.[0]}{selectedClient.last_name?.[0]}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>{selectedClient.first_name} {selectedClient.last_name}</p>
                <p className="text-xs" style={{ color: "var(--text-muted)" }}>{selectedClient.client_type} {selectedClient.email ? `- ${selectedClient.email}` : ""}</p>
              </div>
              <button onClick={() => { setSelectedClient(null); setFirstName(""); setLastName(""); setClientType("Private"); setEmail(""); setPhone(""); }} className="text-xs font-medium" style={{ color: "var(--danger)" }}>Change</button>
            </div>
          ) : (
            <div className="relative mb-5">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "var(--text-muted)" }} />
              <Input
                value={clientSearch} onChange={(e) => setClientSearch(e.target.value)}
                placeholder="Search existing clients..." className="pl-10 h-11 rounded-lg"
                style={{ border: "1px solid var(--border)" }} data-testid="client-search-input"
              />
              {clientResults.length > 0 && (
                <div className="absolute z-50 top-full left-0 right-0 mt-1 rounded-lg shadow-lg max-h-52 overflow-y-auto" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
                  {clientResults.map(c => (
                    <button key={c.id} onClick={() => selectClient(c)} className="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-slate-50" style={{ borderBottom: "1px solid var(--border)" }}>
                      <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0" style={{ background: "var(--primary)" }}>
                        {c.first_name?.[0]}{c.last_name?.[0]}
                      </div>
                      <div>
                        <p className="text-sm font-medium">{c.first_name} {c.last_name}</p>
                        <p className="text-xs" style={{ color: "var(--text-muted)" }}>{c.client_type}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <FormField label="First Name *" value={firstName} onChange={setFirstName} placeholder="First name" testId="new-job-first-name" />
            <FormField label="Last Name *" value={lastName} onChange={setLastName} placeholder="Last name" testId="new-job-last-name" />
            <div>
              <Label className="text-[11px] font-medium tracking-wide uppercase mb-1.5 block" style={{ color: "var(--text-muted)" }}>Client Type *</Label>
              <div className="flex gap-2">
                <Select value={clientType} onValueChange={setClientType}>
                  <SelectTrigger className="h-11 rounded-lg flex-1" style={{ border: "1px solid var(--border)" }} data-testid="new-job-client-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{clientTypes.map(ct => <SelectItem key={ct} value={ct}>{ct}</SelectItem>)}</SelectContent>
                </Select>
                <button onClick={() => { setNewTypeName(""); setNewTypeCommission("none"); setShowAddClientType(true); }} className="h-11 px-3 rounded-lg text-xs font-semibold flex items-center gap-1" style={{ border: "1px solid var(--border)", color: "var(--primary)" }} data-testid="add-client-type-btn">
                  <Plus className="w-3.5 h-3.5" /> New
                </button>
              </div>
            </div>
            <div>
              <Label className="text-[11px] font-medium tracking-wide uppercase mb-1.5 block" style={{ color: "var(--text-muted)" }}>Works Type *</Label>
              <Select value={worksType || ""} onValueChange={setWorksType}>
                <SelectTrigger className="h-11 rounded-lg" style={{ border: "1px solid var(--border)" }} data-testid="new-job-works-type"><SelectValue placeholder="Select works type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Quote Request">Quote Request</SelectItem>
                  <SelectItem value="Call Out">Call Out</SelectItem>
                  <SelectItem value="Works Order">Works Order</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </section>

        {/* Address Section — Currys style */}
        <section className="ui-surface p-6 mb-6" data-testid="new-job-address-section">
          <h2 className="text-sm font-semibold tracking-wide uppercase mb-5" style={{ color: "var(--text-secondary)" }}>
            <MapPin className="w-4 h-4 inline mr-2" style={{ color: "var(--text-muted)" }} /> Site Address
          </h2>

          {addressMode === "postcode" && (
            <>
              <div className="flex gap-3 mb-3">
                <div className="flex-1">
                  <Input
                    value={postcode} onChange={(e) => { setPostcode(e.target.value); setPostcodeError(""); }}
                    placeholder="Postcode *" className="h-12 rounded-lg text-base"
                    autoComplete="nope"
                    style={{ border: postcodeError ? "2px solid var(--danger)" : "1px solid var(--border)" }}
                    onKeyDown={(e) => e.key === "Enter" && findAddress()}
                    data-testid="new-job-postcode"
                  />
                  {postcodeError && <p className="text-xs mt-1.5" style={{ color: "var(--danger)" }}>{postcodeError}</p>}
                </div>
                <Button onClick={findAddress} disabled={searching} className="h-12 px-6 rounded-lg gap-1.5" style={{ background: "var(--primary)", color: "#fff" }} data-testid="find-address-btn">
                  <Search className="w-4 h-4" /> {searching ? "Searching..." : "Find address"}
                </Button>
              </div>

              {/* Address results dropdown */}
              {addressResults.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs font-medium mb-2" style={{ color: "var(--text-secondary)" }}>
                    {addressResults.length} address{addressResults.length !== 1 ? "es" : ""} found — select one:
                  </p>
                  <div className="rounded-lg max-h-72 overflow-y-auto" style={{ border: "1px solid var(--border)", background: "var(--surface)" }}>
                    {addressResults.map((addr, i) => (
                      <button key={i} onClick={() => selectAddress(addr)} className="w-full text-left px-4 py-3 text-sm transition-colors hover:bg-blue-50" style={{ borderBottom: i < addressResults.length - 1 ? "1px solid var(--border)" : "none" }} data-testid={`address-result-${i}`}>
                        <span style={{ color: "var(--text-primary)" }}>{addr.display}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <button onClick={() => setAddressMode("manual")} className="text-sm font-medium underline" style={{ color: "var(--text-secondary)" }} data-testid="enter-manually-btn">
                Enter address manually
              </button>
            </>
          )}

          {(addressMode === "manual" || addressMode === "found") && (
            <>
              {addressMode === "found" && (
                <div className="flex items-center justify-between mb-4">
                  <p className="text-xs font-medium" style={{ color: "var(--success)" }}>Address selected from postcode lookup</p>
                  <button onClick={() => { setAddressMode("postcode"); setAddressResults([]); setAddressLine1(""); setAddressLine2(""); }} className="text-xs font-medium" style={{ color: "var(--primary)" }}>Change address</button>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-4">
                <div className="relative">
                  <Input value={addressLine1} onChange={(e) => { setAddressLine1(e.target.value); searchAddressAutocomplete(e.target.value); }} placeholder="Address 1 *" className="h-12 rounded-lg text-base" autoComplete="nope" style={{ border: "1px solid var(--border)" }} data-testid="new-job-address" />
                  {addressSuggestions.length > 0 && (
                    <div className="absolute z-50 top-full left-0 right-0 mt-1 rounded-lg shadow-lg max-h-52 overflow-y-auto" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
                      {addressSuggestions.map((s, i) => (
                        <button key={i} onClick={() => { setAddressLine1(s.line1); setAddressLine2(s.line2 || ""); setCity(s.city || city); setCounty(s.county || county); if (s.postcode) setPostcode(s.postcode); setAddressSuggestions([]); }} className="w-full text-left px-4 py-3 text-sm transition-colors hover:bg-blue-50" style={{ borderBottom: "1px solid var(--border)" }} data-testid={`address-suggest-${i}`}>
                          <span className="font-medium" style={{ color: "var(--text-primary)" }}>{s.line1}</span>
                          {s.city && <span style={{ color: "var(--text-muted)" }}>, {s.city}</span>}
                          {s.postcode && <span style={{ color: "var(--text-muted)" }}> {s.postcode}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <Input value={addressLine2} onChange={(e) => setAddressLine2(e.target.value)} placeholder="Address 2 (optional)" className="h-12 rounded-lg text-base" autoComplete="nope" style={{ border: "1px solid var(--border)" }} data-testid="new-job-address2" />
                </div>
                <div>
                  <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Town / City *" className="h-12 rounded-lg text-base" autoComplete="nope" style={{ border: "1px solid var(--border)" }} data-testid="new-job-city" />
                </div>
                <div>
                  <Input value={county} onChange={(e) => setCounty(e.target.value)} placeholder="County (optional)" className="h-12 rounded-lg text-base" autoComplete="nope" style={{ border: "1px solid var(--border)" }} data-testid="new-job-county" />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex-1 max-w-xs">
                  <Input value={postcode} onChange={(e) => setPostcode(e.target.value)} placeholder="Postcode *" className="h-12 rounded-lg text-base" autoComplete="nope" style={{ border: "1px solid var(--border)" }} data-testid="new-job-postcode-manual" />
                </div>
                <button onClick={() => { setAddressMode("postcode"); setAddressResults([]); }} className="text-sm font-medium underline" style={{ color: "var(--text-secondary)" }}>
                  Search by postcode
                </button>
              </div>
            </>
          )}
        </section>

        {/* Contact Details */}
        <section className="ui-surface p-6 mb-6" data-testid="new-job-contact-section">
          <h2 className="text-sm font-semibold tracking-wide uppercase mb-5" style={{ color: "var(--text-secondary)" }}>
            <Phone className="w-4 h-4 inline mr-2" style={{ color: "var(--text-muted)" }} /> Contact Details
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <FormField label="Correspondence Email *" value={email} onChange={setEmail} placeholder="email@example.com" type="email" testId="new-job-email" />
            <FormField label="Correspondence Phone" value={phone} onChange={setPhone} placeholder="07xxx xxx xxx" testId="new-job-phone" />
            <FormField label="Tenant Phone (optional)" value={tenantPhone} onChange={setTenantPhone} placeholder="07xxx xxx xxx" testId="new-job-tenant-phone" />
          </div>
        </section>

        {/* Job Description */}
        <section className="ui-surface p-6 mb-6" data-testid="new-job-description-section">
          <h2 className="text-sm font-semibold tracking-wide uppercase mb-4" style={{ color: "var(--text-secondary)" }}>
            <FileText className="w-4 h-4 inline mr-2" style={{ color: "var(--text-muted)" }} /> Job Description *
          </h2>
          <textarea
            value={jobDetails} onChange={(e) => setJobDetails(e.target.value)}
            placeholder="Brief description of the job, scope of work, special instructions..."
            rows={5}
            className="w-full rounded-lg p-4 text-sm resize-y focus:outline-none transition-colors"
            style={{ border: "1px solid var(--border)", color: "var(--text-primary)" }}
            data-testid="new-job-description"
          />
        </section>

        {/* Submit */}
        <div className="flex justify-end gap-3 mb-8">
          <Button variant="outline" onClick={() => navigate(-1)} className="h-10 px-5 rounded-lg" style={{ border: "1px solid var(--border)" }}>Cancel</Button>
          <Button onClick={handleCreate} disabled={saving} className="btn-primary gap-2 h-10 px-6 rounded-lg" data-testid="new-job-submit-bottom">
            <Plus className="w-4 h-4" /> {saving ? "Creating..." : "Create Job"}
          </Button>
        </div>
      </div>

      {/* Add Client Type Dialog */}
      <Dialog open={showAddClientType} onOpenChange={setShowAddClientType}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add New Client Type</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-medium uppercase tracking-wide block mb-1.5" style={{ color: "var(--text-muted)" }}>Name *</Label>
              <Input value={newTypeName} onChange={(e) => setNewTypeName(e.target.value)} placeholder="e.g. Foxtons, Savills" className="h-10 rounded-lg" style={{ border: "1px solid var(--border)" }} />
            </div>
            <div>
              <Label className="text-xs font-medium uppercase tracking-wide block mb-1.5" style={{ color: "var(--text-muted)" }}>Client Category *</Label>
              <select value={newTypeCategory} onChange={(e) => setNewTypeCategory(e.target.value)} className="w-full h-10 rounded-lg text-sm px-3" style={{ border: "1px solid var(--border)" }}>
                <option value="private">Private (client name shown on PDF)</option>
                <option value="corporate">Corporate (company name shown on PDF)</option>
              </select>
            </div>
            <div>
              <Label className="text-xs font-medium uppercase tracking-wide block mb-1.5" style={{ color: "var(--text-muted)" }}>Commission</Label>
              <select value={newTypeCommission} onChange={(e) => setNewTypeCommission(e.target.value)} className="w-full h-10 rounded-lg text-sm px-3" style={{ border: "1px solid var(--border)" }}>
                <option value="none">0% — No Commission (same as Private)</option>
                <option value="12.5+vat">12.5% + VAT (same as Hamptons)</option>
                <option value="20+vat">20% + VAT (same as Bairstow Eves)</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddClientType(false)}>Cancel</Button>
            <Button onClick={async () => {
              const name = newTypeName.trim();
              if (!name) { toast.error("Name required"); return; }
              if (clientTypes.includes(name)) { toast.error("Already exists"); return; }
              const rate = newTypeCommission === "none" ? 54 : (newTypeCommission === "12.5+vat" ? 64 : 67);
              try {
                const existing = await getPricingSettings();
                existing[name] = { hourly_rate: rate, commission_type: newTypeCommission, materials_rule: newTypeCommission === "none" ? "private" : "commission", client_category: newTypeCategory };
                await updatePricingSettings(existing);
                setClientType(name);
                setShowAddClientType(false);
                getConfig().then(setConfig);
                toast.success(`"${name}" added`);
              } catch { toast.error("Failed to save"); }
            }} className="btn-primary gap-1">
              <Plus className="w-4 h-4" /> Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FormField({ label, value, onChange, placeholder, type = "text", testId }) {
  return (
    <div>
      <Label className="text-[11px] font-medium tracking-wide uppercase mb-1.5 block" style={{ color: "var(--text-muted)" }}>{label}</Label>
      <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} autoComplete="nope" className="h-11 rounded-lg" style={{ border: "1px solid var(--border)" }} data-testid={testId} />
    </div>
  );
}
